import Button from "components/button";
import ImportExport from "components/import-export";
import { useToastContext } from "hooks/useToastContext";
import moment from "moment";
import intl from "react-intl-universal";
import {
  getProductionPlanSupplierExport,
  getProductionPlanSupplierExportModel,
} from "services/production-plan-supplier.service";
import { downloadFile } from "utils/custom-functions";
import { formatErrorMessage } from "utils/handle-error";
import { useProductionPlanSupplierContext } from "../../context";
import type { ProductionPlanSupplierFilters } from "../../types";
import {
  buildProductionPlanSupplierImportPayload,
  getProductionPlanSupplierFiltersValidationError,
} from "../../utils";
import ProductionPlanSupplierFilterFields from "../production-plan-supplier-filter-fields";
import * as S from "./styled";

interface ProductionPlanSupplierImportExportProps {
  isOpen: boolean;
  close: () => Promise<void>;
}

const ProductionPlanSupplierImportExport = ({
  isOpen,
  close,
}: ProductionPlanSupplierImportExportProps) => {
  const toastContext = useToastContext();
  const openToast = toastContext?.openToast ?? (() => undefined);
  const {
    filters,
    filterOptionsLoading,
    supplierOptions,
    materialOptions,
    familyOptions,
    labeledFamilyOptions,
    handleChangeFilter,
    startLoading,
    stopLoading,
  } = useProductionPlanSupplierContext();

  const validateFilters = (filtersToValidate: ProductionPlanSupplierFilters) => {
    const validationError = getProductionPlanSupplierFiltersValidationError(filtersToValidate);

    if (validationError) {
      openToast(validationError, "negative");
      return false;
    }

    return true;
  };

  const processError = (error: unknown) => {
    openToast(formatErrorMessage(error), "negative");
  };

  const exportData = async (filtersToExport: ProductionPlanSupplierFilters) => {
    if (!validateFilters(filtersToExport)) {
      return;
    }

    startLoading();
    try {
      const response = await getProductionPlanSupplierExport(filtersToExport);
      const fileDate = moment(new Date()).format("YYYYMMDD_HHmmss");
      downloadFile(`${fileDate}_supplierProductionPlan.csv`, "csv", response);
    } catch (error) {
      processError(error);
    } finally {
      stopLoading();
    }
  };

  const exportModel = async () => {
    startLoading();
    try {
      const response = await getProductionPlanSupplierExportModel();
      downloadFile("supplierProductionPlan_model.csv", "csv", response);
    } catch (error) {
      processError(error);
    } finally {
      stopLoading();
    }
  };

  const modalContent = (
    <S.ModalFilterContent>
      <ProductionPlanSupplierFilterFields
        filters={filters}
        supplierOptions={supplierOptions}
        materialOptions={materialOptions}
        familyOptions={familyOptions}
        labeledFamilyOptions={labeledFamilyOptions}
        onChange={handleChangeFilter}
        filterOptionsLoading={filterOptionsLoading}
      />

      <S.ModalFilterAction>
        <Button
          buttonType="secondary"
          className="button-download"
          onClick={() => exportData(filters)}
          value={intl.get("exportImportModal.butttons.download")}
        />
      </S.ModalFilterAction>
    </S.ModalFilterContent>
  );

  return isOpen ? (
    <ImportExport
      title={intl.get("exportImportModal.supplierProductionPlan.title")}
      contextModal="supplierProductionPlan"
      open={isOpen}
      handleClose={close}
      downloadModelo={exportModel}
      importArquivo="supplier-production-plans/import-data"
      downloadArquivo={() => exportData(filters)}
      buildImportPayload={buildProductionPlanSupplierImportPayload}
      type="CSV"
      content={{
        hasFilter: true,
        component: modalContent,
      }}
    />
  ) : null;
};

export default ProductionPlanSupplierImportExport;
